import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogActions, Button, Typography, Box, MobileStepper, DialogTitle } from '@mui/material'
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material'
import { CloseIconButton } from './Utilities'
import { useTranslation } from 'react-i18next'
import { useKeycloak } from './Keycloak'

const STORAGE_KEY = 'btv_whatsnew_seen'

export default function WhatsNewDialog() {

    const { hasRole, isLoggedIn } = useKeycloak()
    const { i18n } = useTranslation()

    const lang = i18n.language?.slice(0, 2) || 'de'
    
    const [data, setData] = useState(null)
    const [dismissed, setDismissed] = useState(false)
    const [activeStep, setActiveStep] = useState(0)
    
    useEffect(() => {
        fetch('/whats-new.json')
            .then(res => {
                if (!res.ok) throw new Error()
                return res.json()
            })
            .then(json => {
                if (!json?.version || !json?.features?.length) return
                setData(json)
            })
            .catch(error => console.warn("[WhatsNewDialog] Failed to fetch JSON:", error))
    }, [])

    // filter features by role - no role = visible to all, with role = only if user has it
    const allVisible = data?.features?.filter(elem => !elem.role || (isLoggedIn() && hasRole(elem.role))) || []

    // parse "version:count" from localStorage to determine already seen features
    const stored = window.localStorage.getItem(STORAGE_KEY) || ''
    const [storedVersion, storedCount] = stored.split(':')
    const alreadySeen = storedVersion === data?.version ? parseInt(storedCount) || 0 : 0

    // only show features the user has not seen yet
    const features = allVisible.slice(alreadySeen)
    const shouldShow = data && features.length > 0 && !dismissed

    const handleClose = () => {
        window.localStorage.setItem(STORAGE_KEY, `${data.version}:${allVisible.length}`)
        setDismissed(true)
    }

    if (!shouldShow) return null

    const feature = features[activeStep]
    const maxSteps = features.length
    const t = (elem) => elem?.[lang] || elem?.de || ''

    return (
        <Dialog
            open={shouldShow}
            onClose={handleClose}
            PaperProps={{ sx: { textAlign: 'center', maxWidth: 350 } }}
        >
            <CloseIconButton onClick={handleClose} />

            <DialogTitle sx={{ userSelect: "none", fontSize: "1.3rem" }}>
                {t(data.title)}
            </DialogTitle>

            <DialogContent sx={{ pb: 0 }}>

                <Typography
                    variant="body1"
                    color="text.primary"
                    dangerouslySetInnerHTML={{ __html: t(feature.description) }}
                    sx={{ mb: 2 }}
                />

                {feature.image && (
                    <Box sx={{ mb: 1 }}>
                        <img
                            src={feature.image}
                            alt=""
                            style={{ maxWidth: '100%', borderRadius: 4 }}
                        />
                    </Box>
                )}

            </DialogContent>

            {maxSteps > 1 && (
                <MobileStepper
                    variant="dots"
                    steps={maxSteps}
                    position="static"
                    activeStep={activeStep}
                    sx={{ bgcolor: 'transparent', px: 2, py: 0 }}
                    nextButton={
                        <Button onClick={() => setActiveStep(elem => elem + 1)} disabled={activeStep === maxSteps - 1}>
                            {t(data.next)}
                            <KeyboardArrowRight />
                        </Button>
                    }
                    backButton={
                        <Button  onClick={() => setActiveStep(elem => elem - 1)} disabled={activeStep === 0}>
                            <KeyboardArrowLeft />
                            {t(data.previous)}
                        </Button>
                    }
                />
            )}

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button variant="contained" onClick={handleClose} fullWidth>
                    {t(data.button)}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
